import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShieldAlert, ChevronDown } from 'lucide-react';
import { companyInfo } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const navItems = [
    { name: 'Forside', path: '/' },
    { name: 'Service', path: '/service' },
    { name: 'Om mig', path: '/om-os' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to fetch');
      })
      .then(data => setServices(data))
      .catch(err => console.error('Error fetching services for dropdown:', err));
  }, []);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close sidebar on route change
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location]);

  // Close desktop dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${
      isScrolled 
        ? 'bg-white border-zinc-200/80 shadow-md' 
        : 'bg-white border-zinc-200'
    } text-zinc-900`}>

      <div className={`mx-auto flex max-w-7xl flex-col lg:flex-row items-center justify-between px-4 lg:px-8 gap-4 lg:gap-0 transition-all duration-300 ${
        isScrolled ? 'py-2 lg:py-3' : 'py-4 lg:py-5'
      }`}>
        {/* Top row: Logo and Mobile Menu Button */}
        <div className="flex w-full items-center justify-between lg:w-auto">
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <img 
              src="https://assets.auxodev.dk/assets/ac914a03-55b8-406d-88cf-a09072b72053/4cdafb94-63f9-4079-af03-b7854085fc33.jpg" 
              alt="LJR Teknik ApS Logo" 
              className="h-10 md:h-14 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-zinc-500 hover:text-zinc-900 md:hidden focus:outline-none"
            aria-label="Toggle Menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Desktop & Tablet Navigation and CTA */}
        <div className="hidden md:flex flex-col md:flex-row md:w-full lg:w-auto items-start md:items-center justify-between lg:justify-end gap-4 md:gap-6 lg:gap-8 border-t md:border-t-0 pt-3 md:pt-0 border-zinc-100">
          {/* Menu links left-aligned on tablet */}
          <nav className="flex items-center gap-6 lg:gap-8">
            {navItems.map((item) => {
              if (item.path === '/service') {
                return (
                  <div
                    key={item.path}
                    ref={dropdownRef}
                    className="relative py-2 md:py-4"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={`flex items-center gap-1 text-sm font-bold tracking-wider uppercase transition-colors focus:outline-none cursor-pointer ${
                        isActive(item.path) ? 'text-zinc-900' : 'text-zinc-600'
                      } hover:text-red-600`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {dropdownOpen && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-72 z-50">
                        {/* Invisible bridge to prevent mouse-leave when moving to dropdown */}
                        <div className="absolute top-0 left-0 right-0 h-2" />
                        
                        <div className="rounded-xl border border-zinc-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
                          {/* Small decorative arrow */}
                          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-white border-t border-l border-zinc-200 rotate-45 z-[-1]" />
                          
                          <div className="max-h-80 overflow-y-auto space-y-1">
                            {/* "Se alle serviceydelser" at the top */}
                            <Link
                              to="/service"
                              onClick={() => setDropdownOpen(false)}
                              className="block rounded-lg px-4 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 transition-colors border-b border-zinc-100 mb-1"
                            >
                              Se alle serviceydelser →
                            </Link>

                            {services.length > 0 ? (
                              services.map((service) => {
                                const title = service.title?.da || service.title || '';
                                const slug = service.slug?.da || service.slug || service.id;
                                return (
                                  <Link
                                    key={service.id}
                                    to={`/service/${slug}`}
                                    onClick={() => setDropdownOpen(false)}
                                    className="block rounded-lg px-4 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                                  >
                                    {title}
                                  </Link>
                                );
                              })
                            ) : (
                              <div className="px-4 py-2 text-xs text-zinc-400">Indlæser ydelser...</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-bold tracking-wider uppercase transition-colors hover:text-red-600 ${
                    isActive(item.path) ? 'text-zinc-900' : 'text-zinc-600'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="flex items-center gap-4 shrink-0">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300 transition-all shadow-md hover:shadow-lg"
            >
              <Phone className="h-4 w-4 text-black" />
              <span>{companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Sidebar Menu */}
      <div className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        {/* Backdrop overlay */}
        <div 
          className="fixed inset-0 bg-zinc-950/60 backdrop-blur-xs"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Sidebar panel */}
        <div className={`fixed inset-y-0 right-0 w-full max-w-xs bg-white shadow-2xl flex flex-col z-50 transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-100">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-2">
              <img 
                src="https://assets.auxodev.dk/assets/ac914a03-55b8-406d-88cf-a09072b72053/4cdafb94-63f9-4079-af03-b7854085fc33.jpg" 
                alt="LJR Teknik ApS Logo" 
                className="h-10 w-auto object-contain"
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-zinc-500 hover:text-zinc-900 focus:outline-none"
              aria-label="Close Menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            <nav className="flex flex-col space-y-2">
              {navItems.map((item) => {
                if (item.path === '/service') {
                  return (
                    <div key={item.path} className="space-y-1">
                      <div className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-zinc-50">
                        <Link
                          to="/service"
                          onClick={() => setIsOpen(false)}
                          className={`text-base font-bold tracking-wider uppercase transition-colors hover:text-red-600 ${
                            isActive(item.path) ? 'text-zinc-900' : 'text-zinc-600'
                          }`}
                        >
                          {item.name}
                        </Link>
                        <button
                          onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                          className="p-1.5 text-zinc-500 hover:text-red-600 focus:outline-none"
                        >
                          <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>

                      {/* Mobile Dropdown Items */}
                      {mobileDropdownOpen && (
                        <div className="pl-4 space-y-1 border-l-2 border-zinc-900 ml-4">
                          {services.map((service) => {
                            const title = service.title?.da || service.title || '';
                            const slug = service.slug?.da || service.slug || service.id;
                            return (
                              <Link
                                key={service.id}
                                to={`/service/${slug}`}
                                onClick={() => setIsOpen(false)}
                                className="block rounded-lg px-3 py-1.5 text-sm font-semibold text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                              >
                                {title}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`rounded-lg px-3 py-2.5 text-base font-bold tracking-wider uppercase transition-colors hover:bg-zinc-50 hover:text-red-600 ${
                      isActive(item.path) ? 'bg-zinc-50 text-zinc-900' : 'text-zinc-600'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-zinc-100 bg-zinc-50 flex flex-col gap-3">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center justify-center gap-2 rounded-lg bg-white border border-zinc-200 py-3 text-sm font-medium text-zinc-900 hover:bg-zinc-100 shadow-xs"
            >
              <Phone className="h-4 w-4 text-red-600" />
              <span>Ring op: {companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
